import React from "react";
import ModelEditor from "../components/forms/Editor";
import { Alert, Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { createModel, deleteModel, getModelById, updateModel } from "../services/modelService";
import { Context } from "../App";


export default function CreateModel() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);
    const { deconnect } = React.useContext(Context);
    const [loading, setLoading] = React.useState(isEdit);
    const [error, setError] = React.useState("");
    const [initialModel, setInitialModel] = React.useState(null);

    React.useEffect(() => {
        if (!isEdit) return;
        let active = true;

        const run = async () => {
            try {
                const model = await getModelById(id);
                if (!active) return;
                setInitialModel(model);
            } catch (e) {
                if (!active) return;
                if (e.message === "SESSION_EXPIRED") {
                    alert("Votre session a expiré. Veuillez vous reconnecter.");
                    deconnect();
                    return;
                }
                setError("Erreur lors du chargement du modèle.");
            } finally {
                if (active) setLoading(false);
            }
        };

        run();
        return () => {
            active = false;
        };
    }, [id, isEdit, deconnect]);

    const handleSave = async (data) => {
        try {
            if (isEdit) {
                await updateModel(id, {
                    ...data,
                    description: initialModel?.description || "",
                });
                alert("Modèle mis à jour.");
            } else {
                await createModel({ ...data, description: "" });
                alert("Modèle sauvegardé. Vous pouvez maintenant générer un design depuis ce modèle.");
            }
            return true;
        } catch (e) {
            if (e.message === "SESSION_EXPIRED") {
                alert("Votre session a expiré. Veuillez vous reconnecter.");
                deconnect();
                return false;
            }
            if (isEdit) {
                alert("Erreur lors de la mise à jour du modèle. Veuillez réessayer.");
            } else {
                alert("Erreur lors de la sauvegarde du modèle. Veuillez réessayer.");
            }
            return false;
        }
    };

    const handleDelete = async () => {
        if (!isEdit) {
            alert("Le modèle n'est pas encore sauvegardé.");
            return;
        }

        const ok = window.confirm("Supprimer ce modèle ?");
        if (!ok) return;

        try {
            await deleteModel(id);
            alert("Modèle supprimé.");
            navigate("/model");
        } catch (e) {
            if (e.message === "SESSION_EXPIRED") {
                alert("Votre session a expiré. Veuillez vous reconnecter.");
                deconnect();
                return;
            }
            alert("Erreur lors de la suppression du modèle.");
        }
    };

    const handleCreateNewFromMenu = async () => {
        window.location.assign("/model/new");
    };

    if (loading) {
        return (
            <Box sx={{ p: 4, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    return (
        <ModelEditor
            mode="model"
            initialTitle={initialModel?.title || "Nouveau modèle"}
            initialElements={initialModel?.elements || []}
            initialCanvasBg={initialModel?.canvasBg || "#ffffff"}
            initialCanvasSize={initialModel?.canvasSize || { width: 1080, height: 1080 }}
            onSave={handleSave}
            onDeleteCurrent={handleDelete}
            onCreateNew={handleCreateNewFromMenu}
        />
    );
}
